import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import GlassCard from '../components/GlassCard';
import { familiesAPI, peopleAPI } from '../services/api';
import { parseDateComponents, formatDateForDisplay, getMonth, getDay, getYear, getMonthNameFull } from '../utils/dateUtils';

const ReportsPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [allPeople, setAllPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initialize with current date
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1));
  const [selectedDay, setSelectedDay] = useState(String(now.getDate()));
  const [selectedYear, setSelectedYear] = useState('');

  useEffect(() => {
    loadFamilies();
  }, []);

  useEffect(() => {
    if (selectedFamily) {
      loadPeople(selectedFamily);
    } else {
      loadAllPeople();
    }
  }, [selectedFamily]);

  const loadFamilies = async () => {
    try {
      const response = await familiesAPI.list();
      setFamilies(response.data.families || []);
      if (response.data.families?.length > 0) {
        setSelectedFamily(response.data.families[0].id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load families');
    }
  };

  const loadPeople = async (familyId) => {
    try {
      setLoading(true);
      const response = await peopleAPI.list(familyId);
      setAllPeople(response.data.people || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const loadAllPeople = async () => {
    try {
      setLoading(true);
      const allPeopleList = [];
      const response = await familiesAPI.list();
      const familiesList = response.data.families || [];
      
      for (const family of familiesList) {
        try {
          const peopleResponse = await peopleAPI.list(family.id);
          const people = peopleResponse.data.people || [];
          allPeopleList.push(...people);
        } catch (e) {
          // Skip families we can't access
        }
      }
      
      setAllPeople(allPeopleList);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  // Report 1: Members by Last Name (case-insensitive)
  const membersByLastName = () => {
    const counts = {};
    const nameMap = {}; // Map lowercase to original case for display
    allPeople.forEach(person => {
      const lastName = person.last_name || 'Unknown';
      const lastNameLower = lastName.toLowerCase();
      if (!counts[lastNameLower]) {
        counts[lastNameLower] = 0;
        nameMap[lastNameLower] = lastName; // Store original case
      }
      counts[lastNameLower] = (counts[lastNameLower] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([nameLower, count]) => ({ name: nameMap[nameLower], count }))
      .sort((a, b) => b.count - a.count);
  };

  // Report 2: Members by Location (case-insensitive)
  const membersByLocation = () => {
    const counts = {};
    const locationMap = {}; // Map lowercase to original case for display
    allPeople.forEach(person => {
      const location = [person.city, person.country].filter(Boolean).join(', ') || 'Unknown';
      const locationLower = location.toLowerCase();
      if (!counts[locationLower]) {
        counts[locationLower] = 0;
        locationMap[locationLower] = location; // Store original case
      }
      counts[locationLower] = (counts[locationLower] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([locationLower, count]) => ({ location: locationMap[locationLower], count }))
      .sort((a, b) => b.count - a.count);
  };

  // Report 3: Members by Month and Day (Birthday)
  const membersByMonthDay = () => {
    if (!selectedMonth || !selectedDay) return [];
    
    const monthNum = parseInt(selectedMonth);
    const dayNum = parseInt(selectedDay);
    
    return allPeople.filter(person => {
      if (!person.date_of_birth) return false;
      const components = parseDateComponents(person.date_of_birth);
      if (!components) return false;
      return components.month === monthNum && components.day === dayNum;
    });
  };

  // Report 4: Members by Birth Year
  const membersByBirthYear = () => {
    const counts = {};
    allPeople.forEach(person => {
      if (person.date_of_birth) {
        const year = getYear(person.date_of_birth);
        if (year) {
          counts[year] = (counts[year] || 0) + 1;
        }
      }
    });
    return Object.entries(counts)
      .map(([year, count]) => ({ year: parseInt(year), count }))
      .sort((a, b) => b.year - a.year);
  };

  const lastNameReport = membersByLastName();
  const locationReport = membersByLocation();
  const monthDayReport = membersByMonthDay();
  const birthYearReport = membersByBirthYear();

  return (
    <div style={{ width: '100%', maxWidth: '100%', padding: '0 16px' }} className="reports-page">
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '16px',
        justifyContent: 'space-between', 
        alignItems: 'stretch', 
        marginBottom: '24px' 
      }}>
        <h1 style={{ 
          fontSize: 'clamp(24px, 5vw, 36px)', 
          color: theme.text.primary,
          margin: 0,
        }}>
          Reports
        </h1>
      </div>

      {/* Date Search - Moved to Top */}
      <GlassCard style={{ marginBottom: '24px', padding: 'clamp(16px, 4vw, 24px)' }}>
        <h2 style={{ 
          fontSize: 'clamp(18px, 4vw, 20px)', 
          marginBottom: '16px', 
          color: theme.text.primary 
        }}>
          Search by Birth Date
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', 
          gap: '12px', 
          marginBottom: '16px' 
        }}
        className="date-search-grid"
        >
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: theme.text.secondary, 
              fontSize: 'clamp(13px, 2.5vw, 14px)' 
            }}>
              Month
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '6px',
                border: theme.glass.border,
                background: theme.card.background,
                color: theme.text.primary,
                fontSize: '16px',
                minHeight: '44px',
              }}
            >
              <option value="">Select Month</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                return (
                  <option key={month} value={month}>
                    {monthNames[month - 1]}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: theme.text.secondary, 
              fontSize: 'clamp(13px, 2.5vw, 14px)' 
            }}>
              Day
            </label>
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '6px',
                border: theme.glass.border,
                background: theme.card.background,
                color: theme.text.primary,
                fontSize: '16px',
                minHeight: '44px',
              }}
            >
              <option value="">Select Day</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>
        </div>
        {selectedMonth && selectedDay && (
          <>
            <div style={{ 
              padding: '12px', 
              borderRadius: '6px', 
              background: theme.accent.primary + '10',
              border: `1px solid ${theme.accent.primary}30`,
              marginBottom: '16px',
            }}>
              <div style={{ 
                fontSize: 'clamp(14px, 3vw, 16px)', 
                fontWeight: 600, 
                color: theme.text.primary, 
                marginBottom: '4px' 
              }}>
                {monthDayReport.length} {monthDayReport.length === 1 ? 'member' : 'members'} found
              </div>
              <div style={{ 
                fontSize: 'clamp(12px, 2.5vw, 14px)', 
                color: theme.text.secondary 
              }}>
                Born on {(() => {
                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                  return `${monthNames[parseInt(selectedMonth) - 1]} ${parseInt(selectedDay)}`;
                })()}
              </div>
            </div>
            
            {/* Members Matched to Date - Results Display Right Below */}
            {monthDayReport.length > 0 ? (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: theme.glass.border }}>
                <h3 style={{ 
                  fontSize: 'clamp(16px, 3vw, 18px)', 
                  marginBottom: '12px', 
                  color: theme.text.primary 
                }}>
                  Members Born on {(() => {
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                    return `${monthNames[parseInt(selectedMonth) - 1]} ${parseInt(selectedDay)}`;
                  })()}
                </h3>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', 
                  gap: '12px' 
                }}
                className="report-grid"
                >
                  {monthDayReport.map((person) => (
                    <Link
                      key={person.id}
                      to={`/people/${person.id}`}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'block',
                      }}
                    >
                      <div
                        style={{
                          padding: 'clamp(10px, 2.5vw, 12px)',
                          borderRadius: '8px',
                          background: theme.card.background,
                          border: theme.glass.border,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = theme.accent.primary + '10';
                          e.currentTarget.style.borderColor = theme.accent.primary + '50';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = theme.glass.shadow;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = theme.card.background;
                          e.currentTarget.style.borderColor = theme.glass.border;
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {person.picture_url ? (
                          <img
                            src={person.picture_url}
                            alt={person.preferred_name || person.first_name}
                            style={{
                              width: 'clamp(40px, 8vw, 50px)',
                              height: 'clamp(40px, 8vw, 50px)',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: theme.glass.border,
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 'clamp(40px, 8vw, 50px)',
                              height: 'clamp(40px, 8vw, 50px)',
                              borderRadius: '50%',
                              background: theme.card.background,
                              border: theme.glass.border,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 'clamp(20px, 4vw, 24px)',
                              flexShrink: 0,
                            }}
                          >
                            👤
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontSize: 'clamp(14px, 3vw, 16px)', 
                            fontWeight: 600, 
                            color: theme.text.primary,
                            wordBreak: 'break-word',
                          }}>
                            {person.preferred_name || person.first_name} {person.last_name || ''}
                          </div>
                          {person.date_of_birth && (
                            <div style={{ 
                              fontSize: 'clamp(11px, 2.5vw, 12px)', 
                              color: theme.text.secondary, 
                              marginTop: '4px' 
                            }}>
                              Born: {formatDateForDisplay(person.date_of_birth)}
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ 
                padding: '12px', 
                borderRadius: '6px', 
                background: theme.card.background,
                border: theme.glass.border,
                marginTop: '16px',
              }}>
                <p style={{ 
                  color: theme.text.secondary,
                  fontSize: 'clamp(13px, 2.5vw, 14px)',
                  margin: 0,
                }}>
                  No members found for this date
                </p>
              </div>
            )}
          </>
        )}
      </GlassCard>

      {families.length > 0 && (
        <GlassCard style={{ marginBottom: '24px', padding: 'clamp(16px, 4vw, 24px)' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              color: theme.text.secondary,
              fontSize: 'clamp(13px, 2.5vw, 14px)',
            }}
          >
            Filter by Family (or select "All Families"):
          </label>
          <select
            value={selectedFamily || ''}
            onChange={(e) => setSelectedFamily(e.target.value || null)}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: theme.glass.border,
              background: theme.card.background,
              color: theme.text.primary,
              fontSize: '16px',
              minHeight: '44px',
            }}
          >
            <option value="">All Families</option>
            {families.map((family) => (
              <option key={family.id} value={family.id}>
                {family.name}
              </option>
            ))}
          </select>
        </GlassCard>
      )}

      {error && (
        <GlassCard style={{ 
          marginBottom: '24px', 
          padding: 'clamp(16px, 4vw, 24px)',
          background: 'rgba(255, 0, 0, 0.1)' 
        }}>
          <p style={{ 
            color: theme.text.primary,
            fontSize: 'clamp(14px, 3vw, 16px)',
            wordBreak: 'break-word',
          }}>
            Error: {error}
          </p>
        </GlassCard>
      )}

      {loading ? (
        <GlassCard style={{ 
          textAlign: 'center', 
          padding: 'clamp(32px, 8vw, 48px)' 
        }}>
          <p style={{ 
            color: theme.text.secondary,
            fontSize: 'clamp(14px, 3vw, 16px)',
          }}>
            Loading reports...
          </p>
        </GlassCard>
      ) : (
        <>
          {/* Report 1: Members by Last Name */}
          <GlassCard style={{ 
            marginBottom: '24px',
            padding: 'clamp(16px, 4vw, 24px)',
          }}>
            <h2 style={{ 
              fontSize: 'clamp(18px, 4vw, 24px)', 
              marginBottom: '16px', 
              color: theme.text.primary 
            }}>
              Members by Last Name
            </h2>
            {lastNameReport.length > 0 ? (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', 
                gap: '12px' 
              }}
              className="report-grid"
              >
                {lastNameReport.map(({ name, count }) => (
                  <div
                    key={name}
                    style={{
                      padding: 'clamp(10px, 2.5vw, 12px)',
                      borderRadius: '8px',
                      background: theme.accent.primary + '10',
                      border: `1px solid ${theme.accent.primary}30`,
                    }}
                  >
                    <div style={{ 
                      fontSize: 'clamp(16px, 3vw, 18px)', 
                      fontWeight: 600, 
                      color: theme.text.primary, 
                      marginBottom: '4px',
                      wordBreak: 'break-word',
                    }}>
                      {name}
                    </div>
                    <div style={{ 
                      fontSize: 'clamp(20px, 4vw, 24px)', 
                      fontWeight: 700, 
                      color: theme.accent.primary 
                    }}>
                      {count}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ 
                color: theme.text.secondary,
                fontSize: 'clamp(13px, 2.5vw, 14px)',
              }}>
                No data available
              </p>
            )}
          </GlassCard>

          {/* Report 2: Members by Location */}
          <GlassCard style={{ 
            marginBottom: '24px',
            padding: 'clamp(16px, 4vw, 24px)',
          }}>
            <h2 style={{ 
              fontSize: 'clamp(18px, 4vw, 24px)', 
              marginBottom: '16px', 
              color: theme.text.primary 
            }}>
              Members by Location
            </h2>
            {locationReport.length > 0 ? (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 250px), 1fr))', 
                gap: '12px' 
              }}
              className="report-grid"
              >
                {locationReport.map(({ location, count }) => (
                  <div
                    key={location}
                    style={{
                      padding: 'clamp(10px, 2.5vw, 12px)',
                      borderRadius: '8px',
                      background: theme.accent.primary + '10',
                      border: `1px solid ${theme.accent.primary}30`,
                    }}
                  >
                    <div style={{ 
                      fontSize: 'clamp(13px, 2.5vw, 14px)', 
                      fontWeight: 600, 
                      color: theme.text.primary, 
                      marginBottom: '4px',
                      wordBreak: 'break-word',
                    }}>
                      {location}
                    </div>
                    <div style={{ 
                      fontSize: 'clamp(20px, 4vw, 24px)', 
                      fontWeight: 700, 
                      color: theme.accent.primary 
                    }}>
                      {count}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ 
                color: theme.text.secondary,
                fontSize: 'clamp(13px, 2.5vw, 14px)',
              }}>
                No data available
              </p>
            )}
          </GlassCard>


          {/* Report 4: Members by Birth Year */}
          <GlassCard style={{ 
            marginBottom: '24px',
            padding: 'clamp(16px, 4vw, 24px)',
          }}>
            <h2 style={{ 
              fontSize: 'clamp(18px, 4vw, 24px)', 
              marginBottom: '16px', 
              color: theme.text.primary 
            }}>
              Members by Birth Year
            </h2>
            {birthYearReport.length > 0 ? (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 150px), 1fr))', 
                gap: '12px' 
              }}
              className="report-grid"
              >
                {birthYearReport.map(({ year, count }) => (
                  <div
                    key={year}
                    style={{
                      padding: 'clamp(10px, 2.5vw, 12px)',
                      borderRadius: '8px',
                      background: theme.accent.primary + '10',
                      border: `1px solid ${theme.accent.primary}30`,
                    }}
                  >
                    <div style={{ 
                      fontSize: 'clamp(16px, 3vw, 18px)', 
                      fontWeight: 600, 
                      color: theme.text.primary, 
                      marginBottom: '4px' 
                    }}>
                      {year}
                    </div>
                    <div style={{ 
                      fontSize: 'clamp(20px, 4vw, 24px)', 
                      fontWeight: 700, 
                      color: theme.accent.primary 
                    }}>
                      {count}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ 
                color: theme.text.secondary,
                fontSize: 'clamp(13px, 2.5vw, 14px)',
              }}>
                No data available
              </p>
            )}
          </GlassCard>
        </>
      )}
    </div>
  );
};

export default ReportsPage;

